import React from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getTheme, Icon, TooltipHost } from '@fluentui/react';
import { RootState } from '../../store/rootReducer';
import { IGettingStartedStep, IGettingStartedStepItem } from '../../models/GettingStarted';
import { useSetGettingStartedStepStatusMutation } from '../../store/endpoints/GettingStartedEndpoints';
import GettingStartedPanel from './GettingStartedPanel';
import { IGettingStartedStepAction } from './GettingStartedChecklist';
import { CHECKLIST_STRINGS } from './wizardStrings';
import useGettingStarted from './useGettingStarted';

// The nav-bar entry point. It owns the panel's open state, the polling read model, and the
// per-step CTA map, in the same spirit as TasksDropdown.
//
// The checklist component below it stays container agnostic and navigation free, so everything
// that knows about routes lives right here.

const theme = getTheme();

/** localStorage key for the auto-open flag. Scoped per organization so switching org can prompt again. */
const seenStorageKey = (organizationId: string) => `ac-getting-started-seen-${organizationId}`;

const readSeen = (organizationId: string): boolean => {
    try {
        return window.localStorage.getItem(seenStorageKey(organizationId)) === 'true';
    } catch {
        // Private browsing or a blocked storage partition. Fail closed: never auto-open.
        return true;
    }
};

const writeSeen = (organizationId: string) => {
    try {
        window.localStorage.setItem(seenStorageKey(organizationId), 'true');
    } catch {
        // Nothing to do. Worst case the panel auto-opens again on the next load.
    }
};

/** Compact ring showing completed steps out of total, with a check once everything is done. */
const ProgressRing: React.FC<{ complete: number, total: number, attention: boolean }> = ({ complete, total, attention }) => {
    const size = 24;
    const stroke = 2.5;
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const ratio = total > 0 ? complete / total : 0;
    const allComplete = total > 0 && complete === total;

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={theme.palette.neutralLight}
                    strokeWidth={stroke}
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={attention ? theme.palette.orange : theme.palette.themePrimary}
                    strokeWidth={stroke}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={circumference * (1 - ratio)}
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
            </svg>
            {allComplete
                ? <Icon iconName="CheckMark" style={{ fontSize: '12px', color: theme.palette.themePrimary }} />
                : <span style={{ fontSize: '12px', color: theme.palette.neutralPrimary }}>{`${complete}/${total}`}</span>}
        </div>
    );
};

const GettingStartedButton: React.FC = () => {
    const history = useHistory();
    const selectedOrganization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const gettingStarted = useGettingStarted();
    const [setStepStatus] = useSetGettingStartedStepStatusMutation();
    const [open, setOpen] = React.useState(false);
    const autoOpenedFor = React.useRef<string | undefined>(undefined);

    // Auto-open once per organization, and only while there is still something to do. The flag is
    // written the moment we open, so a dismissal (or simply reloading the page) never brings it
    // back on its own. The user can always reopen it from the nav bar.
    React.useEffect(() => {
        if (!selectedOrganization) return;
        if (autoOpenedFor.current === selectedOrganization) return;
        if (!gettingStarted.isVisible || gettingStarted.allComplete) return;
        if (readSeen(selectedOrganization)) {
            autoOpenedFor.current = selectedOrganization;
            return;
        }
        autoOpenedFor.current = selectedOrganization;
        writeSeen(selectedOrganization);
        setOpen(true);
    }, [selectedOrganization, gettingStarted.isVisible, gettingStarted.allComplete]);

    const navigate = (step: IGettingStartedStep, path: string) => {
        // Tell the server the user has picked the step up. Best effort: navigation must not wait on
        // it and a failure here is not worth interrupting onboarding for.
        if (step.status === 'available') {
            setStepStatus({ id: step.id, status: 'inProgress' });
        }
        setOpen(false);
        history.push(path);
    };

    // Opening one of the connections listed under a step.
    //
    // A distributor goes back into the guided flow, on that distributor, rather than to the folio
    // settings panel. Clicking an unfinished connection means "carry on with this", and the raw settings
    // panel is not where they left off — it is the screen the wizard exists to keep them out of. The
    // wizard picks up from what is actually stored, so this survives a reload and a new session.
    //
    // Anything else is an installed folio and nothing more, so it keeps the same settings link the
    // Integration Health widget uses, landing on the credential tab.
    const openStepItem = (step: IGettingStartedStep, item: IGettingStartedStepItem) => {
        setOpen(false);
        if (step.id === 'connect-distributors' && item.sourceId) {
            // sourceId, not id: the wizard is keyed by the folio being connected, while item.id is this
            // organization's installation of it.
            history.push(`/settings/extensions?distributorSetup=${encodeURIComponent(item.sourceId)}`);
            return;
        }
        history.push(
            `/settings/extensions?panel=extensionFolio&instance=${encodeURIComponent(item.id)}&panelTab=edit`);
    };

    // Per-step-id CTA map. Steps this build does not recognise render without an action rather
    // than sending the user somewhere arbitrary.
    const getStepAction = (step: IGettingStartedStep): IGettingStartedStepAction | undefined => {
        if (step.status === 'locked') return undefined;
        // A step that tracks several connections keeps its action after going green, because "add
        // another distributor" is a thing the operator still wants from a complete row.
        if (step.status === 'complete' && !(step.items?.length)) return undefined;
        switch (step.id) {
            case 'storefront-setup': {
                // The New Storefront flow lives on the storefront list page. When background work
                // is live or waiting on review, carry the session id so that page can deep link.
                const base = '/settings/storefront/storefronts';
                const path = step.activeSessionId
                    ? `${base}?generationSession=${encodeURIComponent(step.activeSessionId)}`
                    : base;
                return {
                    onClick: () => navigate(step, path),
                    isProminent: step.status === 'needsReview'
                };
            }
            case 'crm-integration':
                // The guided setup dialog lives on the extensions page, which is also where the user
                // ends up if they want the full folio settings afterwards.
                return { onClick: () => navigate(step, '/settings/extensions?crmSetup=1') };
            case 'configure-integrations':
                // Same dialog, opened past the connecting part: this step is the half that decides what
                // the connection does, and by now its suggested settings have had time to arrive.
                return { onClick: () => navigate(step, '/settings/extensions?crmConfigure=1') };
            case 'connect-distributors':
                // Straight to the install catalog, which is where another distributor gets added. The
                // connections already set up are listed on the row itself and open their own settings.
                return {
                    onClick: () => navigate(step, '/settings/extensions?distributorSetup=1'),
                    isProminent: step.status === 'needsReview'
                };
            case 'favorite-products':
                // Product search already has the star UI, so there is nothing bespoke to build.
                return { onClick: () => navigate(step, '/search') };
            default:
                return undefined;
        }
    };

    if (!gettingStarted.isVisible) {
        return null;
    }

    const tooltip = gettingStarted.allComplete
        ? CHECKLIST_STRINGS.allCompleteTitle
        : CHECKLIST_STRINGS.progressLabel(gettingStarted.completeCount, gettingStarted.total);

    return (<>
        <TooltipHost content={tooltip}>
            <div
                role="button"
                aria-label={CHECKLIST_STRINGS.buttonAriaLabel}
                onClick={() => setOpen(!open)}
                style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                    padding: '0 8px'
                }}
            >
                <ProgressRing
                    complete={gettingStarted.completeCount}
                    total={gettingStarted.total}
                    attention={gettingStarted.needsReview}
                />
            </div>
        </TooltipHost>
        <GettingStartedPanel
            isOpen={open}
            onDismiss={() => setOpen(false)}
            steps={gettingStarted.steps}
            isLoading={gettingStarted.isLoading}
            getStepAction={getStepAction}
            onOpenStepItem={openStepItem}
        />
    </>);
};

export default GettingStartedButton;
